import { HttpErrorResponse } from '@angular/common/http';

import { Action, createReducer, on } from '@ngrx/store';
import { PageMetasActions } from './page-metas.actions';
import { RootActions } from '@store/root.actions';

import { LinkObject, Footer, MainNav, PageMetas } from '@models/interfaces';
import { DEFAULT_CMS_PAGE_METAS, TLanguage, LanguagesEnum } from '@core/config';

export interface PageMetasState {
  selectedLanguage: TLanguage;
  metas: PageMetas;
  nav: { main: MainNav.LanguageConfig; footer: Footer.LanguageConfig };
  widget: { enabled: boolean; link?: LinkObject };
  updated: number | null;
  loading: boolean;
  error: HttpErrorResponse | string | null;
}

export const initialState: PageMetasState = {
  selectedLanguage: LanguagesEnum.EN,
  metas: DEFAULT_CMS_PAGE_METAS,
  nav: { main: null, footer: null },
  widget: { enabled: false },
  updated: null,
  loading: false,
  error: null,
};

export type State = Readonly<typeof initialState>;

const pageMetasReducer = createReducer(
  initialState,
  on(RootActions.rehydrate, (state, action) => ({ ...state, ...action.state.pageMetas })),
  on(PageMetasActions.clear, state => ({ ...state, metas: { ...initialState.metas } })),
  on(PageMetasActions.setLanguage, (state, { language }) => ({ ...state, selectedLanguage: language })),
  on(PageMetasActions.setMetas, (state, { payload, merge }) => ({
    ...state,
    metas: merge ? { ...state.metas, ...payload } : payload,
    updated: Date.now(),
  })),

  // FETCH

  on(PageMetasActions.fetchMainNavItems, PageMetasActions.fetchFooterNavItems, state => ({
    ...state,
    loading: true,
    error: null,
  })),

  on(PageMetasActions.fetchMainNavItemsSuccess, (state, { payload }) => ({
    ...state,
    nav: payload ? { ...state.nav, main: { ...state.nav.main, ...payload } } : state.nav,
    loading: false,
  })),

  on(PageMetasActions.fetchFooterNavItemsSuccess, (state, { payload }) => ({
    ...state,
    nav: { ...state.nav, footer: payload },
    loading: false,
  })),

  // WIDGET

  on(PageMetasActions.updateWidgetSettings, (state, settings) => ({
    ...state,
    widget: { enabled: settings.enabled, link: settings.link ?? null },
  })),

  // MISC

  on(PageMetasActions.setLoading, (state, { loading }) => ({ ...state, loading })),

  // ERRORS

  on(
    PageMetasActions.fetchMainNavItemsError,
    PageMetasActions.fetchFooterNavItemsError,
    PageMetasActions.fetchFooterNavItemsError,
    (state, { error }) => ({ ...state, error, loading: false })
  )
);

export const reducer = (state: State | undefined, action: Action) => {
  return pageMetasReducer(state, action);
};
